// The four route states (`APP_STYLE` §12: "every route needs its four states —
// loading, empty, denied, not-found — built before the happy path"). Resolution
// lives here rather than in App.svelte so that every route answers the same
// question the same way, and so the answer is testable without a browser.
//
// How the states reach the screen:
//
//   loading, denied, not-found  — take over the route. There is nothing useful
//                                 to show beside them.
//   empty                       — renders *in place*, inside the route's own
//                                 layout. An operator with no invoices still
//                                 needs the button that creates one, and a payer
//                                 looking at a page with no reported hours still
//                                 needs to see whose page it is. Replacing the
//                                 whole route would take those away.
//
// `ready` is the fifth return value: not a state to render, the absence of one.

export const ROUTE_STATES = Object.freeze({
  LOADING: 'loading',
  DENIED: 'denied',
  NOT_FOUND: 'notFound',
  EMPTY: 'empty',
  READY: 'ready',
});

// Operator settings are split by scope so the route itself says whether account
// or project state is being edited. The old mixed `/operator/settings` address
// is still accepted by App.svelte as an account-settings alias.
export const ROUTE_KEYS = Object.freeze([
  'home',
  'intro',
  'operator',
  'operatorAccountSettings',
  'operatorNewProject',
  'operatorProjectSettings',
  'invoice',
  'pay',
]);

// The routes a stranger reaches from a shared link. Copy for these is written in
// the payer's voice and may never carry an internal state name.
export const PAYER_ROUTE_KEYS = Object.freeze(['invoice', 'pay']);

export function routeKeyFor(route) {
  if (route?.name === 'operator') {
    if (route.section === 'accountSettings') return 'operatorAccountSettings';
    if (route.section === 'newProject') return 'operatorNewProject';
    if (route.section === 'projectSettings') return 'operatorProjectSettings';
    return 'operator';
  }
  return ROUTE_KEYS.includes(route?.name) ? route.name : 'home';
}

export function isPayerRouteKey(routeKey) {
  return PAYER_ROUTE_KEYS.includes(routeKey);
}

function homeState(context) {
  // Solo instances answer `/` with the token-entry form instead of an index, so
  // there is no list to be empty. The form is the route's content.
  if (context.soloEntry) return ROUTE_STATES.READY;
  if (context.invoiceCount > 0) return ROUTE_STATES.READY;
  // Paging past the last page addresses something that does not exist; an index
  // with nothing in it yet does not.
  return context.page > 1 ? ROUTE_STATES.NOT_FOUND : ROUTE_STATES.EMPTY;
}

function introState(context) {
  return context.sectionCount > 0 ? ROUTE_STATES.READY : ROUTE_STATES.EMPTY;
}

function operatorState(context) {
  // Signed out is not denial — the sign-in form is what this route is for.
  if (!context.signedIn) return ROUTE_STATES.READY;
  if (!context.isOperator) return ROUTE_STATES.DENIED;
  return context.invoiceCount > 0 ? ROUTE_STATES.READY : ROUTE_STATES.EMPTY;
}

function operatorAccountSettingsState(context) {
  // Unlike the desk, there is no signed-out affordance here: settings has no
  // sign-in form of its own, so a visitor who cannot open it is denied and sent
  // to the desk.
  if (!context.signedIn || !context.isOperator || !context.canManage) {
    return ROUTE_STATES.DENIED;
  }
  return ROUTE_STATES.READY;
}

function operatorProjectSettingsState(context) {
  if (!context.signedIn || !context.isOperator || !context.canManage) {
    return ROUTE_STATES.DENIED;
  }
  return context.hasProject ? ROUTE_STATES.READY : ROUTE_STATES.EMPTY;
}

function operatorNewProjectState(context) {
  if (!context.signedIn || !context.isOperator || !context.canManage) {
    return ROUTE_STATES.DENIED;
  }
  return ROUTE_STATES.READY;
}

function invoiceState(context) {
  // A bearer publication answers an unknown token with silence rather than an
  // error, so "no document" is the not-found signal on this route.
  if (!context.invoice) return ROUTE_STATES.NOT_FOUND;
  return context.reportedWorkCount > 0 ? ROUTE_STATES.READY : ROUTE_STATES.EMPTY;
}

function payState(context) {
  if (!context.paymentAttempt) return ROUTE_STATES.NOT_FOUND;
  return context.receiptItemCount > 0 ? ROUTE_STATES.READY : ROUTE_STATES.EMPTY;
}

const ROUTE_RESOLVERS = {
  home: homeState,
  intro: introState,
  operator: operatorState,
  operatorAccountSettings: operatorAccountSettingsState,
  operatorNewProject: operatorNewProjectState,
  operatorProjectSettings: operatorProjectSettingsState,
  invoice: invoiceState,
  pay: payState,
};

/**
 * @param {string} routeKey one of ROUTE_KEYS
 * @param {object} context route inputs; every field defaults to the value that
 *   produces `ready`, so a caller only supplies what its route actually knows.
 * @returns {string} one of ROUTE_STATES
 */
export function resolveRouteState(routeKey, context = {}) {
  const {
    ready = true,
    subscriptionFailed = false,
    pathUnmatched = false,
  } = context;

  // These three come before anything route-specific. `pathUnmatched` is first
  // because it is a pure routing fact: an address the router never claimed
  // cannot be loading, and waiting for data before saying so would leave a
  // skeleton on screen forever. Then: until a subscription is ready we know
  // nothing, and a failed one tells us nothing.
  if (pathUnmatched) return ROUTE_STATES.NOT_FOUND;
  if (!ready) return ROUTE_STATES.LOADING;
  if (subscriptionFailed) return ROUTE_STATES.DENIED;

  const resolve = ROUTE_RESOLVERS[routeKey];
  if (!resolve) return ROUTE_STATES.NOT_FOUND;

  return resolve({
    soloEntry: false,
    signedIn: false,
    isOperator: false,
    canManage: false,
    hasProject: false,
    invoiceCount: 0,
    page: 1,
    sectionCount: 0,
    invoice: null,
    reportedWorkCount: 0,
    paymentAttempt: null,
    receiptItemCount: 0,
    ...context,
  });
}

// Copy for the three states that take over a route. Payer routes speak the
// payer's voice: no internal state name, no provider identifier, and — where a
// payment could be in doubt — an explicit statement that nothing was charged.
const ROUTE_STATE_CONTENT = {
  home: {
    loading: { title: 'Loading open pages' },
    denied: {
      title: "We couldn't load this",
      body: 'Something went wrong reaching the server. Reload the page to try again.',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'Check the link you followed. If someone shared a page with you, open their link instead.',
      actionLabel: 'Go to the start',
    },
    empty: {
      title: 'No open pages yet',
      body: 'When someone reports work and shares it, it appears here.',
    },
  },
  intro: {
    loading: { title: 'Loading how it works' },
    denied: {
      title: "We couldn't load this",
      body: 'Something went wrong reaching the server. Reload the page to try again.',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'How it works lives at /intro.',
      actionLabel: 'Go to how it works',
    },
    empty: {
      title: 'Nothing to explain here yet',
      body: 'This page has no sections to show.',
    },
  },
  operator: {
    loading: { title: 'Loading operator desk' },
    denied: {
      title: "This account isn't an operator",
      body: 'You are signed in, but this account has no operator membership. Ask an owner to add it, or sign out and use another account.',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'The operator desk lives at /operator.',
      actionLabel: 'Go to the operator desk',
    },
    empty: {
      title: 'No work reported yet',
      body: 'Start an invoice to report your first hours.',
    },
  },
  operatorAccountSettings: {
    loading: { title: 'Loading account settings' },
    denied: {
      title: 'Settings need owner or admin access',
      body: 'Sign in as an operator with owner or admin access to change these.',
      actionLabel: 'Go to the operator desk',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'Account settings live at /operator/account-settings.',
      actionLabel: 'Go to the operator desk',
    },
    empty: {
      title: 'Account settings are ready',
      body: 'There is no project selection required for account settings.',
    },
  },
  operatorNewProject: {
    loading: { title: 'Loading new project' },
    denied: {
      title: 'Projects need owner or admin access',
      body: 'Sign in as an operator with owner or admin access to create a project.',
      actionLabel: 'Go to the operator desk',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'Create a project at /operator/projects/new.',
      actionLabel: 'Go to the operator desk',
    },
    empty: {
      title: 'New project',
      body: 'Name the project and choose its starting currency.',
    },
  },
  operatorProjectSettings: {
    loading: { title: 'Loading project settings' },
    denied: {
      title: 'Settings need owner or admin access',
      body: 'Sign in as an operator with owner or admin access to change these.',
      actionLabel: 'Go to the operator desk',
    },
    notFound: {
      title: "There's nothing at this address",
      body: 'Project settings live under /operator/projects.',
      actionLabel: 'Go to the operator desk',
    },
    empty: {
      title: 'Choose a project',
      body: 'Project settings apply to one project at a time.',
      actionLabel: 'Go to the operator desk',
    },
  },
  invoice: {
    loading: { title: 'Loading this page' },
    denied: {
      title: "We couldn't load this page",
      body: 'Something went wrong on our side. Nothing has been charged. Reload the page to try again.',
    },
    notFound: {
      // Unchanged from the U01 payer surface on purpose: this is the sentence a
      // stranger who mistyped a link already reads.
      title: "We couldn't find that page",
      body: 'The link may have a typo, or the person who shared it may have closed the page. Ask them for a fresh link.',
    },
    empty: {
      title: 'No hours have been reported here yet',
      body: 'When the person who did the work reports hours, they appear here to pay for.',
    },
  },
  pay: {
    loading: { title: 'Loading your payment' },
    denied: {
      title: "We couldn't load your payment",
      body: 'Something went wrong on our side. Nothing has been charged. Reload the page to try again.',
    },
    notFound: {
      title: "We couldn't find that payment",
      body: 'The link may have expired or been mistyped. Nothing has been charged. Ask the person who shared the page for a fresh link.',
    },
    empty: {
      title: 'Nothing is itemised on this payment yet',
      body: 'The amount above is what you chose to pay. The breakdown appears once it is recorded.',
    },
  },
};

export function routeStateContent(routeKey, state) {
  return ROUTE_STATE_CONTENT[routeKey]?.[state] || null;
}
